(function () {
  const giftRules = Array.isArray(window.cartTotalOffers) ? window.cartTotalOffers : [];
  if (!giftRules.length) return;

  const GIFT_FLAG_PROPERTY = '_cart_offer_gift';
  const RULE_KEY_PROPERTY = '_cart_offer_rule';
  const EVENT_SOURCE = 'cart-total-offers';
  const offerContext = window.cartTotalOfferContext || {};

  const toNormalizedArray = (value) => {
    if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  };

  const normalizeCodeList = (value) => toNormalizedArray(value).map((item) => item.toUpperCase());

  const normalizeRule = (rule, index) => {
    const threshold = Number(rule && rule.threshold);
    const variants = Array.isArray(rule && rule.variants)
      ? rule.variants.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)
      : [];

    if (!Number.isFinite(threshold) || threshold <= 0 || !variants.length) return null;

    return {
      key: String((rule && rule.key) || `offer-${index + 1}`),
      threshold,
      variants: [...new Set(variants)],
      marketHandles: toNormalizedArray(rule && rule.marketHandles),
      countryCodes: normalizeCodeList(rule && rule.countryCodes),
      currencyCodes: normalizeCodeList(rule && rule.currencyCodes),
    };
  };

  const isRuleAllowedForContext = (rule) => {
    const marketHandle = String(offerContext.marketHandle || '').trim();
    const countryCode = String(offerContext.countryCode || '').trim().toUpperCase();
    const currencyCode = String(offerContext.currencyCode || '').trim().toUpperCase();

    if (rule.marketHandles.length && !rule.marketHandles.includes(marketHandle)) return false;
    if (rule.countryCodes.length && !rule.countryCodes.includes(countryCode)) return false;
    if (rule.currencyCodes.length && !rule.currencyCodes.includes(currencyCode)) return false;

    return true;
  };

  const rules = giftRules
    .map(normalizeRule)
    .filter(Boolean)
    .filter(isRuleAllowedForContext)
    .sort((a, b) => a.threshold - b.threshold);
  if (!rules.length) return;

  let isSyncing = false;
  let syncQueued = false;

  const isTruthyGiftFlag = (value) => value === true || value === 'true' || value === '1' || value === 1;

  const getCartJson = () =>
    fetch(`${window.routes.cart_url}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then((response) => response.json());

  const getDrawerSections = () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function') return [];
    return cartDrawer.getSectionsToRender().map((section) => section.id);
  };

  const updateGiftLines = (updates, sections) =>
    fetch(window.routes.cart_update_url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        sections,
        sections_url: window.location.pathname,
      }),
    }).then((response) => response.json());

  const addGiftVariants = (items, sections) =>
    fetch(window.routes.cart_add_url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items,
        sections,
        sections_url: window.location.pathname,
      }),
    }).then((response) => response.json());

  const renderDrawerFromSections = (responseWithSections) => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || !responseWithSections || !responseWithSections.sections) return false;
    if (typeof cartDrawer.getSectionsToRender !== 'function') return false;

    const getSectionInnerHTML = (html, selector = '.shopify-section') =>
      new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;

    cartDrawer.getSectionsToRender().forEach((section) => {
      const sectionHTML = responseWithSections.sections[section.id];
      if (!sectionHTML) return;

      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!target) return;

      target.innerHTML = getSectionInnerHTML(sectionHTML, section.selector);
    });

    const hasItems = Boolean(
      responseWithSections.items && Array.isArray(responseWithSections.items) && responseWithSections.items.length
    );
    cartDrawer.classList.toggle('is-empty', !hasItems);
    return true;
  };

  const getEligibleSubtotal = (cart) =>
    cart.items.reduce((sum, item) => {
      const isGiftLine = isTruthyGiftFlag(item.properties && item.properties[GIFT_FLAG_PROPERTY]);
      if (isGiftLine) return sum;
      return sum + item.final_line_price;
    }, 0);

  const getDesiredGiftMap = (subtotal) => {
    const desired = new Map();

    rules.forEach((rule) => {
      if (subtotal < rule.threshold) return;
      desired.set(rule.key, new Set(rule.variants));
    });

    return desired;
  };

  const runGiftSync = async () => {
    if (isSyncing) {
      syncQueued = true;
      return;
    }

    isSyncing = true;

    try {
      const drawerSections = getDrawerSections();
      const cart = await getCartJson();
      const desiredGiftMap = getDesiredGiftMap(getEligibleSubtotal(cart));
      const existingGiftKeys = new Set();
      const updates = {};
      const additions = [];

      cart.items.forEach((item) => {
        const properties = item.properties || {};
        const isGiftLine = isTruthyGiftFlag(properties[GIFT_FLAG_PROPERTY]);
        if (!isGiftLine) return;

        const ruleKey = String(properties[RULE_KEY_PROPERTY] || '');
        const desiredVariants = desiredGiftMap.get(ruleKey);
        const variantId = Number(item.variant_id);
        const lookupKey = `${ruleKey}:${variantId}`;

        if (!desiredVariants || !desiredVariants.has(variantId)) {
          updates[item.key] = 0;
          return;
        }

        existingGiftKeys.add(lookupKey);

        if (item.quantity !== 1) {
          updates[item.key] = 1;
        }
      });

      desiredGiftMap.forEach((variantSet, ruleKey) => {
        variantSet.forEach((variantId) => {
          const lookupKey = `${ruleKey}:${variantId}`;
          if (existingGiftKeys.has(lookupKey)) return;
          additions.push({
            id: variantId,
            quantity: 1,
            properties: {
              [GIFT_FLAG_PROPERTY]: 'true',
              [RULE_KEY_PROPERTY]: ruleKey,
            },
          });
        });
      });

      if (!Object.keys(updates).length && !additions.length) return;

      let latestResponse = null;
      if (Object.keys(updates).length) latestResponse = await updateGiftLines(updates, drawerSections);
      if (additions.length) latestResponse = await addGiftVariants(additions, drawerSections);

      const drawerRendered = renderDrawerFromSections(latestResponse);
      if (!drawerRendered && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        const updatedCart = await getCartJson();
        publish(PUB_SUB_EVENTS.cartUpdate, { source: EVENT_SOURCE, cartData: updatedCart });
      }
    } catch (error) {
      console.error('cart-total-offers sync failed', error);
    } finally {
      isSyncing = false;
      if (syncQueued) {
        syncQueued = false;
        runGiftSync();
      }
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(runGiftSync, 100);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === EVENT_SOURCE) return;
      runGiftSync();
    });
  }
})();
